/* Shared Google Directions optimizer — the ONLY place stop ordering is
 * computed. Both the Open House and Brokerage route dialogs call
 * `optimizeRoute`, so they can never drift apart.
 *
 * Strategy: one-way trip with minimum backtracking.
 *   1. Nearest-neighbor (NN) chain from the origin gives a natural
 *      geographic sweep — the *last* stop in that chain becomes the
 *      destination (instead of the old "farthest-from-origin" rule).
 *   2. Every other stop is a waypoint that Google re-orders
 *      (`optimizeWaypoints: true`).  Because the destination is already
 *      the natural endpoint, Google's optimizer can refine the middle
 *      without the zigzag-inducing constraint of reaching a distant
 *      outlier.
 *   3. >26 stops: NN pre-sort → split into ≤25-stop chunks → each chunk
 *      optimized independently → chained (last stop of chunk N = origin
 *      of chunk N+1).
 * One Directions request per chunk.  Anything that isn't a clean, complete
 * answer throws a `RouteOptimizationError` — there is no silent fallback. */

import { gmapsAuthFailed, mapsAuthExplanation, rejectOnGmapsAuthFailure } from "./gmapsAuth";

export const MAX_OPTIMIZABLE_STOPS = 26; // origin + Google's 25-waypoint cap
export const CHUNK_SIZE = MAX_OPTIMIZABLE_STOPS - 1; // stops per chunk when auto-splitting
export const OPTIMIZE_TIMEOUT_MS = 20000;
export const MODULE_RELOAD_TIMEOUT_MS = 10000;
/* Maps JS lazy-loads these two modules on the first DirectionsService call.
 * If the browser drops or blocks that load, Maps JS never retries: route()
 * hangs once, then resolves `undefined` forever (seen on iPhone Safari).
 * We re-inject them ourselves — see `reloadDirectionsModules`. */
const DIRECTIONS_MODULES = ["geometry", "directions"];
const MODULE_BASE = "https://maps.googleapis.com/maps-api-v3/api/js";

const GOOGLE_STATUSES = [
  "REQUEST_DENIED",
  "OVER_QUERY_LIMIT",
  "ZERO_RESULTS",
  "NOT_FOUND",
  "MAX_WAYPOINTS_EXCEEDED",
  "MAX_ROUTE_LENGTH_EXCEEDED",
  "INVALID_REQUEST",
  "UNKNOWN_ERROR",
];

export const FAILURE_EXPLANATIONS = {
  MAPS_NOT_LOADED: "Google Maps hasn't finished loading in this browser. Reload the page and try again.",
  NO_STOPS: "There are no stops to optimize.",
  NO_ORIGIN: "Pick a start point first.",
  ORIGIN_NOT_FOUND: "Google couldn't place your start point on the map. Try GPS, your home address, or pick a suggestion from the address list.",
  TOO_MANY_STOPS: `More stops than Google can handle, even after auto-splitting. This shouldn't happen — please report it.`,
  MISSING_COORDS: "Some stops have no map coordinates, so Google can't route them. Sync addresses / geocode first, or remove those stops.",
  TIMEOUT: `Google did not answer within ${Math.round(OPTIMIZE_TIMEOUT_MS / 1000)} seconds. Check your connection and any content blockers, then retry.`,
  REQUEST_DENIED: "Google rejected the request. The Directions API is not enabled for this Maps key (Google Cloud Console → APIs & Services → Library → Directions API), or the key's website restriction doesn't match this site.",
  OVER_QUERY_LIMIT: "This Maps key has hit its Directions quota. Retry in a minute or raise the quota in Google Cloud Console.",
  ZERO_RESULTS: "Google found no drivable route from your start point to these stops. If you're on GPS, your reported position may be wrong — try Home or a custom address.",
  NOT_FOUND: "Google couldn't locate one of the stops or the start point.",
  MAX_WAYPOINTS_EXCEEDED: "Too many stops for a single Google request. Remove some stops.",
  MAX_ROUTE_LENGTH_EXCEEDED: "The route is too long for Google to compute in one request.",
  INVALID_REQUEST: "Google rejected the request as malformed. This is an app bug and has been logged.",
  UNKNOWN_ERROR: "Google's Directions server returned an internal error. Retry in a few seconds.",
  BAD_WAYPOINT_ORDER: "Google answered but returned an unusable stop order. This has been logged — retry, and reorder manually if it repeats.",
  DIRECTIONS_MODULE_FAILED:
    "Google Maps' Directions module never loaded in this browser tab — a content blocker or a dropped network request on this device stopped it, and Google doesn't retry on its own. We re-requested it and it still didn't arrive. Reload the page; if this keeps happening on this device only, check content blockers / Screen Time web filters for maps.googleapis.com.",
};

export class RouteOptimizationError extends Error {
  constructor(code, message, extra = {}) {
    super(message || code);
    this.name = "RouteOptimizationError";
    this.code = code;
    Object.assign(this, extra);
  }
}

export function googleErrorCode(err) {
  if (err instanceof RouteOptimizationError) return err.code;
  const code = typeof err?.code === "string" ? err.code.toUpperCase() : "";
  if (GOOGLE_STATUSES.includes(code)) return code;
  const msg = String(err?.message || err || "").toUpperCase();
  return GOOGLE_STATUSES.find((s) => msg.includes(s)) || "UNKNOWN_ERROR";
}

export function explainFailure(code) {
  if (code === "MAPS_AUTH_FAILED") return mapsAuthExplanation();
  return FAILURE_EXPLANATIONS[code] || `Google returned ${code}.`;
}

function authFailedError() {
  return new RouteOptimizationError("MAPS_AUTH_FAILED", "Google Maps rejected this site's API key (gm_authFailure)");
}

/* ds.route() never settles once Google has rejected the key, so race it
 * against the auth-failure signal — that turns a 20s hang into an instant,
 * precise error. */
function routeCall(ds, request) {
  if (gmapsAuthFailed()) return Promise.reject(authFailedError());
  return Promise.race([ds.route(request), rejectOnGmapsAuthFailure(authFailedError)]);
}

export function hasCoords(p) {
  return (
    !!p &&
    typeof p.lat === "number" &&
    typeof p.lng === "number" &&
    !Number.isNaN(p.lat) &&
    !Number.isNaN(p.lng)
  );
}

export function haversineKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export function isPermutation(order, n) {
  if (!Array.isArray(order) || order.length !== n) return false;
  if (new Set(order).size !== n) return false;
  return order.every((i) => Number.isInteger(i) && i >= 0 && i < n);
}

export function pickFarthest(originPt, stops) {
  return stops.reduce(
    (best, s) => (!best || haversineKm(originPt, s) > haversineKm(originPt, best) ? s : best),
    null,
  );
}

export function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(
      () =>
        reject(
          new RouteOptimizationError(
            "TIMEOUT",
            `Google Directions did not respond within ${Math.round(ms / 1000)}s`,
          ),
        ),
      ms,
    );
    Promise.resolve(promise).then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });
}

export function formatDuration(seconds) {
  const mins = Math.round((seconds || 0) / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/**
 * Greedy nearest-neighbor ordering — used as a pre-sort when the stop
 * count exceeds Google's 25-waypoint limit.
 */
export function nearestNeighborOrder(originPt, stops) {
  const remaining = stops.map((s, i) => ({ ...s, _origIdx: i }));
  const ordered = [];
  let current = originPt;
  while (remaining.length > 0) {
    let nearestIdx = 0;
    let nearestDist = Infinity;
    for (let i = 0; i < remaining.length; i++) {
      const d = haversineKm(current, remaining[i]);
      if (d < nearestDist) {
        nearestDist = d;
        nearestIdx = i;
      }
    }
    ordered.push(remaining[nearestIdx]);
    current = remaining[nearestIdx];
    remaining.splice(nearestIdx, 1);
  }
  return ordered;
}

function travelModeFor(g, mode) {
  const tm = g.TravelMode || {};
  if (mode === "walking") return tm.WALKING || "WALKING";
  if (mode === "bicycling") return tm.BICYCLING || "BICYCLING";
  if (mode === "transit") return tm.TRANSIT || "TRANSIT";
  return tm.DRIVING || "DRIVING";
}

function toGoogleError(err) {
  if (err instanceof RouteOptimizationError) return err;
  return new RouteOptimizationError(googleErrorCode(err), String(err?.message || err || ""));
}

function latLngOf(loc) {
  if (!loc) return null;
  const lat = typeof loc.lat === "function" ? loc.lat() : loc.lat;
  const lng = typeof loc.lng === "function" ? loc.lng() : loc.lng;
  return hasCoords({ lat, lng }) ? { lat, lng } : null;
}

/* Names of Maps JS modules the browser has finished fetching (resource
 * timing only records completed loads, so blocked/aborted/hung requests
 * are simply absent). */
export function loadedMapsModules() {
  try {
    return performance
      .getEntriesByType("resource")
      .map((e) => e.name)
      .filter((n) => n.includes("/maps-api-v3/api/js/"))
      .map((n) => n.split("/").pop().replace(/\.js.*$/, ""));
  } catch (_) {
    return [];
  }
}

export function mapsDiagnostics(g) {
  return { maps_version: (g && g.version) || "", loaded_modules: loadedMapsModules() };
}

function injectScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve(src);
    s.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(s);
  });
}

/* Re-request the directions modules from Google's CDN using the exact
 * version Maps JS is running (e.g. "3.66.4d" → /66/4d/directions.js). Only
 * modules that never finished loading are fetched; if resource timing is
 * unavailable we fall back to just `directions`. */
export async function reloadDirectionsModules(g, timeoutMs = MODULE_RELOAD_TIMEOUT_MS) {
  const parts = String((g && g.version) || "").split(".");
  if (parts.length < 3 || typeof document === "undefined") {
    throw new RouteOptimizationError("DIRECTIONS_MODULE_FAILED", `cannot derive module URL from Maps version "${g && g.version}"`);
  }
  const loaded = new Set(loadedMapsModules());
  const missing = DIRECTIONS_MODULES.filter((m) => !loaded.has(m));
  const targets = missing.length ? missing : ["directions"];
  try {
    await withTimeout(
      Promise.all(targets.map((m) => injectScript(`${MODULE_BASE}/${parts[1]}/${parts[2]}/${m}.js`))),
      timeoutMs,
    );
  } catch (e) {
    throw new RouteOptimizationError("DIRECTIONS_MODULE_FAILED", String((e && e.message) || e || ""));
  }
  return targets;
}

/* ds.route() with two failure modes Maps JS itself never surfaces:
 *   · resolves `undefined` → its directions module failed to load
 *   · hangs → the module (or the JSONP answer) is stuck in flight
 * In both cases we re-inject the module once and retry. A hang with the
 * module confirmed loaded is a genuine TIMEOUT. */
async function routeWithRecovery(g, ds, request, timeoutMs, diag) {
  let response;
  try {
    response = await withTimeout(routeCall(ds, request), timeoutMs);
  } catch (e) {
    const code = googleErrorCode(e);
    if (code !== "TIMEOUT" || loadedMapsModules().includes("directions")) throw toGoogleError(e);
    diag.timed_out_before_module = true;
  }
  if (response && response.routes) return response;

  diag.recovery_attempted = true;
  diag.reloaded_modules = await reloadDirectionsModules(g);
  try {
    response = await withTimeout(routeCall(ds, request), timeoutMs);
  } catch (e) {
    throw toGoogleError(e);
  }
  if (!response || !response.routes) {
    throw new RouteOptimizationError("DIRECTIONS_MODULE_FAILED", "DirectionsService still returned no response after the module reload");
  }
  diag.recovered = true;
  return response;
}

/* Address-string origins get snapped to coordinates with one cheap 1-leg
 * Directions call so the "farthest stop" pick has a real point to
 * measure from. Coordinate origins pass straight through. */
async function resolveOrigin(g, ds, origin, firstStop, gmMode, timeoutMs, diag) {
  if (hasCoords(origin)) return { lat: origin.lat, lng: origin.lng };
  let res;
  try {
    res = await routeWithRecovery(
      g,
      ds,
      {
        origin,
        destination: { lat: firstStop.lat, lng: firstStop.lng },
        travelMode: gmMode,
      },
      timeoutMs,
      diag,
    );
  } catch (e) {
    const code = googleErrorCode(e);
    throw new RouteOptimizationError(
      code === "NOT_FOUND" || code === "ZERO_RESULTS" ? "ORIGIN_NOT_FOUND" : code,
      String(e?.message || e || ""),
    );
  }
  const pt = latLngOf(res?.routes?.[0]?.legs?.[0]?.start_location);
  if (!pt) {
    throw new RouteOptimizationError("ORIGIN_NOT_FOUND", "Directions probe returned no start_location");
  }
  return pt;
}

/**
 * @param {object} p
 * @param {{lat:number,lng:number}|string} p.origin  start point
 * @param {Array<{id:string,lat:number,lng:number}>} p.stops  ≥1 stops
 * @param {"driving"|"walking"|"bicycling"|"transit"} [p.travelMode]
 * @param {number} [p.timeoutMs]
 * @returns {Promise<{orderedIds:string[], directions:object, totalSeconds:number, totalMeters:number, origin:object, destinationId:string}>}
 * @throws {RouteOptimizationError}
 */
export async function optimizeRoute({ origin, stops, travelMode = "driving", timeoutMs = OPTIMIZE_TIMEOUT_MS }) {
  const g = typeof window !== "undefined" && window.google && window.google.maps;
  if (!g || !g.DirectionsService) {
    throw new RouteOptimizationError("MAPS_NOT_LOADED", "window.google.maps.DirectionsService unavailable");
  }
  if (gmapsAuthFailed()) throw authFailedError();
  if (!Array.isArray(stops) || stops.length === 0) {
    throw new RouteOptimizationError("NO_STOPS", "No stops to optimize");
  }
  const missing = stops.filter((s) => !hasCoords(s));
  if (missing.length) {
    throw new RouteOptimizationError("MISSING_COORDS", `${missing.length} stop(s) have no lat/lng`, {
      missing: missing.map((s) => s.id),
    });
  }
  if (!origin || (typeof origin === "string" && !origin.trim())) {
    throw new RouteOptimizationError("NO_ORIGIN", "Start point not set");
  }

  const ds = new g.DirectionsService();
  const gmMode = travelModeFor(g, travelMode);
  const diag = mapsDiagnostics(g);

  try {
    const originPt = await resolveOrigin(g, ds, origin, stops[0], gmMode, timeoutMs, diag);

    // Always pre-sort by nearest-neighbor so geographic clusters stay
    // together and the LAST stop in the chain becomes the natural endpoint.
    const sorted = nearestNeighborOrder(originPt, stops);

    // ── Single-chunk (≤26 stops) ──
    if (sorted.length <= MAX_OPTIMIZABLE_STOPS) {
      return await _optimizeSingleChunk(g, ds, originPt, sorted, gmMode, timeoutMs, diag);
    }

    // ── Multi-chunk (>26 stops): auto-split ──
    const chunks = [];
    for (let i = 0; i < sorted.length; i += CHUNK_SIZE) {
      chunks.push(sorted.slice(i, i + CHUNK_SIZE));
    }
    const allOrderedIds = [];
    let totalSeconds = 0;
    let totalMeters = 0;
    let chunkOrigin = originPt;
    let allDirections = null;
    for (const chunk of chunks) {
      const result = await _optimizeSingleChunk(g, ds, chunkOrigin, chunk, gmMode, timeoutMs, { ...diag });
      allOrderedIds.push(...result.orderedIds);
      totalSeconds += result.totalSeconds;
      totalMeters += result.totalMeters;
      allDirections = result.directions;
      const lastId = result.orderedIds[result.orderedIds.length - 1];
      const lastStop = chunk.find((s) => s.id === lastId);
      if (lastStop) chunkOrigin = { lat: lastStop.lat, lng: lastStop.lng };
    }
    return {
      orderedIds: allOrderedIds,
      directions: allDirections,
      totalSeconds,
      totalMeters,
      origin: originPt,
      destinationId: allOrderedIds[allOrderedIds.length - 1],
      diag: { ...diag, chunked: true, chunk_count: chunks.length },
    };
  } catch (e) {
    const err = toGoogleError(e);
    if (!err.diag) err.diag = diag;
    throw err;
  }
}

/* Optimize a single batch of ≤26 stops via Google Directions.
 * Stops are expected to already be in nearest-neighbor order.
 * The LAST stop in the array becomes the destination (natural endpoint
 * of the geographic sweep) — Google then optimizes the intermediate
 * waypoints within that origin→destination corridor. */
async function _optimizeSingleChunk(g, ds, originPt, stops, gmMode, timeoutMs, diag) {
  // Destination = last stop in the NN-sorted array (natural endpoint)
  const destination = stops[stops.length - 1];
  const middle = stops.slice(0, -1);

  const response = await routeWithRecovery(
    g,
    ds,
    {
      origin: originPt,
      destination: { lat: destination.lat, lng: destination.lng },
      waypoints: middle.map((s) => ({ location: { lat: s.lat, lng: s.lng }, stopover: true })),
      optimizeWaypoints: true,
      travelMode: gmMode,
    },
    timeoutMs,
    diag,
  );

  const route = response?.routes?.[0];
  const order = middle.length === 0 ? [] : route?.waypoint_order;
  if (!route || !isPermutation(order, middle.length)) {
    throw new RouteOptimizationError(
      "BAD_WAYPOINT_ORDER",
      `waypoint_order=${JSON.stringify(order ?? null)} for ${middle.length} waypoints (status=${response?.status || "?"})`,
    );
  }
  const legs = route.legs || [];
  return {
    orderedIds: [...order.map((i) => middle[i].id), destination.id],
    directions: response,
    totalSeconds: legs.reduce((a, l) => a + ((l.duration && l.duration.value) || 0), 0),
    totalMeters: legs.reduce((a, l) => a + ((l.distance && l.distance.value) || 0), 0),
    origin: originPt,
    destinationId: destination.id,
    diag,
  };
}
